import { skipToken } from '@reduxjs/toolkit/query';
import { type AssetMetricPoint, useGetAssetMetricsQuery } from '@src/providers/CoinMetricsApiProvider';
import type { SeriesLineOptions } from 'highcharts';
import { useMemo } from 'react';
import { CM_MAX_PAGE_SIZE } from './tokens';

interface UseSignalsTimeseriesParams {
  /** One or more CoinMetrics metric names. Each produces its own chart series. */
  metrics: string[];
  /** Resolved CoinMetrics asset id (e.g. 'btc'). Query is skipped when undefined. */
  cmAssetId: string | undefined;
  /** Number of daily data points to request. `null` fetches the full asset history. */
  limitPerAsset: number | null;
}

interface UseSignalsTimeseriesResult {
  /** One `SeriesLineOptions` per metric, ready to pass to `LineChart`. */
  series: SeriesLineOptions[];
  isFetching: boolean;
  isError: boolean;
}

const EMPTY_SERIES: SeriesLineOptions[] = [];

/** Fetches a CoinMetrics timeseries for one asset and transforms it into Highcharts series data. */
export function useSignalsTimeseries({
  metrics,
  cmAssetId,
  limitPerAsset,
}: UseSignalsTimeseriesParams): UseSignalsTimeseriesResult {
  // Sort before joining so ['A','B'] and ['B','A'] hit the same RTK Query cache entry,
  // and a new array reference with the same contents doesn't trigger a duplicate fetch.
  const metricsParam = useMemo(() => [...metrics].toSorted().join(','), [metrics]);

  const queryArg =
    cmAssetId == null || metrics.length === 0
      ? skipToken
      : {
          assets: cmAssetId,
          metrics: metricsParam,
          frequency: '1d' as const,
          ...(limitPerAsset != null && { limit_per_asset: limitPerAsset }),
          page_size: limitPerAsset ?? CM_MAX_PAGE_SIZE,
          paging_from: 'end' as const,
        };

  const { data: timeseriesData, isFetching, isError } = useGetAssetMetricsQuery(queryArg);

  // metrics must be a stable reference (module-level constant) — inline arrays cause unnecessary re-runs.
  const series = useMemo(() => buildTimeseriesSeries(timeseriesData?.data, metrics), [timeseriesData, metrics]);

  return { series, isFetching, isError };
}

/**
 * Transforms a CoinMetrics timeseries response into one Highcharts line series per metric.
 * Exported for direct unit testing — the hook is a thin RTK Query wrapper around this.
 */
export function buildTimeseriesSeries(points: AssetMetricPoint[] | undefined, metrics: string[]): SeriesLineOptions[] {
  if (!points) {
    return EMPTY_SERIES;
  }

  return metrics.map(metric => {
    const data: [number, number][] = [];
    for (const point of points) {
      const rawValue = point[metric];
      if (rawValue == null) {
        continue;
      }
      const value = Number(rawValue);
      if (!Number.isFinite(value)) {
        continue;
      }
      const timestamp = new Date(point.time).getTime();
      if (!Number.isFinite(timestamp)) {
        continue;
      }
      data.push([timestamp, value]);
    }
    return { type: 'line', name: metric, data } satisfies SeriesLineOptions;
  });
}
